import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import type {AudioCache} from "@khan/guide-core-helpers";
import type {MessageAudio, MessageAudioChunk} from "@khan/guide-types";
import {staticUrl} from "@khan/urls";

import {audioSampleTranscripts} from "./audio-samples.ts";

/** Represents the control interface for audio playback. */
interface AudioPlayback {
    /**
     * A unique identifier for an audio file. It is used to reference a
     * specific entry in the `AudioCache`.
     */
    audioKey: string;

    /** Indicates whether the audio is currently playing. */
    isPlaying: boolean;

    /** Pauses the audio playback. */
    pause: () => void;

    /** Starts the audio playback. */
    play: (audioKey: string) => void;

    /** Stops the audio playback. */
    stop: () => void;

    /** The index of the audio chunk playing. */
    chunkIndex: number;
}

/**
 * Returns a controller for playing back a sequence of audio chunks.
 */
export const useGuideAudioPlayback = (audioMap: AudioCache): AudioPlayback => {
    const [index, setIndex] = useState(0); // index of the current audio chunk.
    const [audioKey, setAudioKey] = useState("");
    const [isPlaying, setIsPlaying] = useState(false);

    const audioElRef = useRef<HTMLAudioElement>(new Audio());
    const bufferRef = useRef<MessageAudio>([]);

    // To prevent DOM exceptions caused by interrupting the audio element's
    // playback, we keep a reference to the last play promise to ensure that
    // the audio element is not unexpectedly interrupted when it plays a new
    // audio file or pauses.
    const playPromiseRef = useRef<Promise<any>>(Promise.resolve());

    bufferRef.current = audioMap.get(audioKey) ?? [];

    const pause = useCallback(() => {
        setIsPlaying(false);
        playPromiseRef.current.then(() => {
            audioElRef.current.pause();
        });
    }, []);

    const stop = useCallback(() => {
        pause();
        // When stopping the audio, we want to reset the audio element so that
        // when it is played again, it starts from the beginning.
        setIndex(0);
        setAudioKey("");
        audioElRef.current.currentTime = 0;
    }, [pause]);

    const play = useCallback(
        (key: string) => {
            setAudioKey((currAudioKey) => {
                // We do these checks inside the setAudioKey function so that
                // a new play() function isn't generated with each audioKey
                // change.
                if (currAudioKey === key) {
                    audioElRef.current.play();
                    return currAudioKey;
                }
                setIndex(0);

                if (audioSampleTranscripts.hasOwnProperty(key)) {
                    loadAudioSample(key, "welcome").then(
                        (chunk: MessageAudioChunk) => {
                            playPromiseRef.current.then(() => {
                                playPromiseRef.current = _playAudioChunk(
                                    audioElRef.current,
                                    chunk,
                                );
                            });
                        },
                    );
                }

                const initialChunk = audioMap.get(key)?.[0];
                if (initialChunk) {
                    playPromiseRef.current.then(() => {
                        playPromiseRef.current = _playAudioChunk(
                            audioElRef.current,
                            initialChunk,
                        );
                    });
                }
                return key;
            });
        },
        [audioMap],
    );

    /**
     * Effect sets up event listeners for the "ended", "pause", and "play"
     * events on the audio element.
     */
    useEffect(() => {
        // Keep reference to the audio element so that we can remove
        // event listeners in the effect cleanup.
        const audioEl = audioElRef.current;

        const onEnded = () => {
            setIndex((i) => {
                // Loop back to the beginning of the audio chunks if we are
                // at the end.
                const nextIndex = (i + 1) % bufferRef.current.length;
                // NOTE (kabir): this used to check for nextIndex being 0 but
                // for the onboarding popup there's not buffer and (sometimes?) no
                // index. So I changed this to check for !nextIndex to account for
                // that and properly change the onboarding popup's icon
                if (!nextIndex) {
                    // We reset the audio key here, so the next time play() is
                    // invoked, it will start from the beginning.
                    stop();
                }
                return nextIndex;
            });
        };

        const onPlay = () => setIsPlaying(true);
        audioEl.addEventListener("ended", onEnded);
        audioEl.addEventListener("play", onPlay);

        return () => {
            audioEl.removeEventListener("ended", onEnded);
            audioEl.removeEventListener("play", onPlay);
        };
    }, [audioKey, stop]);

    /**
     * Effect for handling auto-playing the next audio chunk (indicated by
     * a change in the chunk index).
     */
    useEffect(() => {
        // We never want to auto-play the first audio chunk, so we return here.
        // We rely on the first chunk being played via the play() function.
        if (index === 0) {
            return;
        }

        // If there isn't an audio chunk at the current index, then we don't
        // want to play anything. This can also happen if there is no buffer
        // associated with the current audioKey.
        const audioChunk = bufferRef.current[index];
        if (!audioChunk) {
            return;
        }

        playPromiseRef.current.then(() => {
            playPromiseRef.current = _playAudioChunk(
                audioElRef.current,
                audioChunk,
            );
        });
    }, [index]);

    return useMemo(
        () => ({
            audioKey,
            chunkIndex: index,
            isPlaying,
            pause,
            play,
            stop,
        }),
        [audioKey, isPlaying, play, pause, stop, index],
    );
};

/**
 * Load one of the pre-generated LMNT audio samples for the welcome
 * samples in the Preferences view or for any Audio or Audio API errors
 */
export const loadAudioSample = async (
    sampleId: string,
    sampleType: "welcome" | "error",
): Promise<MessageAudioChunk> => {
    const sampleUrl = staticUrl(
        `/sounds/khanmigo/audio_samples_v2/${sampleType}/${sampleId}.txt`,
    );
    const text = await fetch(sampleUrl).then((r) => r.text());
    return {
        base64Audio: text,
        transcript: audioSampleTranscripts[sampleId][sampleType],
    };
};

/**
 * Sets the audio element's src to the audio chunk's base64 audio string and
 * starts playing the audio.
 */
const _playAudioChunk = (
    audioEl: HTMLAudioElement,
    audioChunk: MessageAudioChunk,
): Promise<void> => {
    if (audioChunk.base64Audio) {
        // Note: When updating an audio element's src, it automatically
        // sets the currentTime to 0.
        audioEl.src = `data:audio/wav;base64,${audioChunk.base64Audio}`;
        return audioEl.play();
    }
    return Promise.resolve();
};
